import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Accessibility, X, RotateCcw } from 'lucide-react';

const DEFAULT_SETTINGS = {
  fontSize: 100,
  highContrast: false,
  highlightLinks: false,
  bigCursor: false
};

export const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('accessibility_settings');
    return saved ? JSON.parse(saved) : DEFAULT_SETTINGS;
  });

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Font size
    root.style.fontSize = `${settings.fontSize}%`;
    
    // High contrast
    if (settings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
    
    // Highlight links
    if (settings.highlightLinks) {
      root.classList.add('highlight-links');
    } else {
      root.classList.remove('highlight-links');
    }
    
    // Big cursor
    if (settings.bigCursor) {
      root.classList.add('big-cursor');
    } else {
      root.classList.remove('big-cursor');
    }
    
    // Save to localStorage
    localStorage.setItem('accessibility_settings', JSON.stringify(settings));
  }, [settings]);

  const decreaseFontSize = () => {
    setSettings(prev => ({
      ...prev,
      fontSize: Math.max(80, prev.fontSize - 10)
    }));
  };

  const increaseFontSize = () => {
    setSettings(prev => ({
      ...prev,
      fontSize: Math.min(150, prev.fontSize + 10)
    }));
  };

  const toggleHighContrast = () => {
    setSettings(prev => ({
      ...prev,
      highContrast: !prev.highContrast
    }));
  };

  const toggleHighlightLinks = () => {
    setSettings(prev => ({
      ...prev,
      highlightLinks: !prev.highlightLinks
    }));
  };

  const toggleBigCursor = () => {
    setSettings(prev => ({
      ...prev,
      bigCursor: !prev.bigCursor
    }));
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 z-50 w-14 h-14 bg-sky-500 hover:bg-sky-400 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
        aria-label="Open accessibility options"
        data-testid="accessibility-widget-btn"
      >
        <Accessibility className="h-7 w-7" />
      </button>

      {/* Widget Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed left-4 bottom-20 z-50 w-80 bg-white rounded-2xl shadow-2xl overflow-hidden"
              data-testid="accessibility-widget-panel"
            >
              {/* Header */}
              <div className="bg-sky-500 text-white p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Accessibility className="h-6 w-6" />
                  <span className="font-semibold text-lg">Accessibility</span>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-white/20 rounded-full transition-colors"
                  aria-label="Close accessibility options"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4">
                {/* Font Size */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="text-sm font-medium text-slate-700 mb-3">Font Size</div>
                  <div className="flex items-center justify-between">
                    <button
                      onClick={decreaseFontSize}
                      disabled={settings.fontSize <= 80}
                      className="w-12 h-12 bg-white border-2 border-slate-200 rounded-xl flex items-center justify-center text-xl font-bold text-slate-700 hover:border-sky-500 hover:text-sky-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Decrease font size"
                    >
                      A-
                    </button>
                    <div className="text-2xl font-bold text-sky-500">
                      {settings.fontSize}%
                    </div>
                    <button
                      onClick={increaseFontSize}
                      disabled={settings.fontSize >= 150}
                      className="w-12 h-12 bg-white border-2 border-slate-200 rounded-xl flex items-center justify-center text-xl font-bold text-slate-700 hover:border-sky-500 hover:text-sky-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Increase font size"
                    >
                      A+
                    </button>
                  </div>
                </div>

                {/* Toggle Options */}
                <div className="space-y-3">
                  {/* High Contrast */}
                  <button
                    onClick={toggleHighContrast}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      settings.highContrast 
                        ? 'bg-sky-50 border-sky-500 text-sky-700' 
                        : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                    }`}
                    aria-pressed={settings.highContrast}
                  >
                    <span className="font-medium">High Contrast</span>
                    <div className={`w-12 h-7 rounded-full transition-colors relative ${
                      settings.highContrast ? 'bg-sky-500' : 'bg-slate-300'
                    }`}>
                      <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        settings.highContrast ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </div>
                  </button>

                  {/* Highlight Links */}
                  <button
                    onClick={toggleHighlightLinks}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      settings.highlightLinks 
                        ? 'bg-sky-50 border-sky-500 text-sky-700' 
                        : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                    }`}
                    aria-pressed={settings.highlightLinks}
                  >
                    <span className="font-medium">Highlight Links</span>
                    <div className={`w-12 h-7 rounded-full transition-colors relative ${
                      settings.highlightLinks ? 'bg-sky-500' : 'bg-slate-300'
                    }`}>
                      <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        settings.highlightLinks ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </div>
                  </button>

                  {/* Big Cursor */}
                  <button
                    onClick={toggleBigCursor}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      settings.bigCursor 
                        ? 'bg-sky-50 border-sky-500 text-sky-700' 
                        : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                    }`}
                    aria-pressed={settings.bigCursor}
                  >
                    <span className="font-medium">Big Cursor</span>
                    <div className={`w-12 h-7 rounded-full transition-colors relative ${
                      settings.bigCursor ? 'bg-sky-500' : 'bg-slate-300'
                    }`}>
                      <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        settings.bigCursor ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </div>
                  </button>
                </div>

                {/* Reset Button */}
                <button
                  onClick={resetSettings}
                  className="w-full flex items-center justify-center gap-2 p-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium transition-colors"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset Settings
                </button>
              </div>

              {/* Footer Link */}
              <div className="border-t border-slate-100 p-4">
                <Link
                  to="/website-accessibility"
                  onClick={() => setIsOpen(false)}
                  className="text-sm text-sky-500 hover:text-sky-600 font-medium flex items-center justify-center gap-2 transition-colors"
                >
                  <Accessibility className="h-4 w-4" />
                  Accessibility Statement
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
